// Global Instructions Rule Applied!
//
// LPs grid state (FUND MANAGEMENT → LPs): the same shape as the LP Allocation
// slice — rows, active filters / sorting, paging — so the canonical grid kit
// (TableView infinite scroll, ViewConfig, user preferences) drives it unchanged.
import { SET_LPS, APPEND_LPS, SET_ACTIVE_FILTERS, SET_ACTIVE_SORTING, UPDATE_LPS_ROW } from './constants.ts'
import { LpRow } from '../../data_models/lps_model.ts'
import { FilterValue, SortConfig } from '../../../app_modules/global_configs/tableViewPreferences.ts'

export interface LpsPaginatedResponse {
  data: LpRow[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface LpsState {
  data: LpRow[]
  totalCount: number
  activeFilters: FilterValue[]
  activeSorting: SortConfig[]
  hasMore: boolean
  currentPage: number
}

type LpsAction =
  | { type: typeof SET_LPS; payload: { response: LpsPaginatedResponse } }
  | { type: typeof APPEND_LPS; payload: { response: LpsPaginatedResponse } }
  | { type: typeof SET_ACTIVE_FILTERS; payload: { filters: FilterValue[] } }
  | { type: typeof SET_ACTIVE_SORTING; payload: { sorting: SortConfig[] } }
  | { type: typeof UPDATE_LPS_ROW; payload: { accountId: string; updates: Partial<LpRow> } }

const initialState: LpsState = {
  data: [],
  totalCount: 0,
  activeFilters: [],
  activeSorting: [],
  hasMore: false,
  currentPage: 1
}

const lpsReducer = (state: LpsState = initialState, action: LpsAction): LpsState => {
  switch (action.type) {
    case SET_LPS: {
      const { response } = action.payload
      return { ...state, data: response.data, totalCount: response.totalCount, hasMore: response.hasMore, currentPage: 1 }
    }
    case APPEND_LPS: {
      const { response } = action.payload
      return {
        ...state,
        data: [...state.data, ...response.data],
        totalCount: response.totalCount,
        hasMore: response.hasMore,
        currentPage: state.currentPage + 1
      }
    }
    case SET_ACTIVE_FILTERS:
      return { ...state, activeFilters: action.payload.filters }
    case SET_ACTIVE_SORTING:
      return { ...state, activeSorting: action.payload.sorting }
    // An inline cell save (Team name) lands on the loaded row without a refetch.
    case UPDATE_LPS_ROW: {
      const { accountId, updates } = action.payload
      if (!state.data.some((r) => r.accounts__id === accountId)) return state
      return { ...state, data: state.data.map((r) => (r.accounts__id === accountId ? { ...r, ...updates } : r)) }
    }
    default:
      return state
  }
}

export default lpsReducer

export const setLps = (payload: { response: LpsPaginatedResponse }) => ({ type: SET_LPS, payload })
export const appendLps = (payload: { response: LpsPaginatedResponse }) => ({ type: APPEND_LPS, payload })
export const setLpsActiveFilters = (payload: { filters: FilterValue[] }) => ({ type: SET_ACTIVE_FILTERS, payload })
export const setLpsActiveSorting = (payload: { sorting: SortConfig[] }) => ({ type: SET_ACTIVE_SORTING, payload })
export const updateLpsRow = (payload: { accountId: string; updates: Partial<LpRow> }) => ({ type: UPDATE_LPS_ROW, payload })
